import { useQueries } from '@tanstack/react-query';
import {
  animalTypesApi, animalsApi, assetTypesApi, breedsApi, coconutTreesApi, customersApi,
  dairyCollectionHubsApi, expenseCategoriesApi, farmMasterApi, feedItemsApi, inventoryItemsApi, inventoryLocationsApi,
  locationsApi, medicineMasterApi, milkSessionsApi, poultryBatchesApi, rolesApi, semenMasterApi, shedMasterApi,
  subscriptionPlansApi, suppliersApi, tasksApi, usersApi, utilityMetersApi, vaccineMasterApi,
} from '../../services/resources';
import { formatDate, formatNumber } from '../../utils/format';

export type RefKey =
  | 'animals' | 'locations' | 'animalTypes' | 'breeds' | 'feedItems' | 'medicines'
  | 'vaccines' | 'expenseCategories' | 'suppliers' | 'customers' | 'users' | 'userNames' | 'roles'
  | 'farms' | 'sheds' | 'assetTypes' | 'inventoryItems' | 'inventoryLocations'
  | 'semenMaster' | 'poultryBatches' | 'coconutTrees' | 'coconutTreeGroups' | 'tasks' | 'utilityMeters'
  | 'dairyCollectionHubs' | 'subscriptionPlans' | 'milkSessions' | 'milkShifts';

export interface RefOption {
  value: string;
  label: string;
  /** Extra facts about the option a field's filterOptions can narrow on. */
  meta?: Record<string, unknown>;
}

const LOADERS: Record<RefKey, () => Promise<RefOption[]>> = {
  // Ascending by Animal ID (COW-000001, COW-000002, ...) - numeric-aware, so
  // COW-000010 sorts after COW-000009.
  animals: async () => (await animalsApi.list({ page_size: 1000 })).items
    .slice()
    .sort((a, b) => a.animal_id.localeCompare(b.animal_id, undefined, { numeric: true }))
    .map((a) => ({ value: a.id, label: `${a.animal_id} — ${a.animal_name ?? a.animal_type_name ?? 'Animal'}` })),
  locations: async () => (await locationsApi.list({ page_size: 500 })).items.map((l) => ({ value: l.id, label: l.name })),
  animalTypes: async () => (await animalTypesApi.list({ page_size: 200 })).items.map((t) => ({ value: t.id, label: t.name })),
  breeds: async () => (await breedsApi.list({ page_size: 500 })).items.map((b) => ({ value: b.id, label: b.name })),
  feedItems: async () => (await feedItemsApi.list({ page_size: 500 })).items.map((f) => ({ value: f.id, label: f.name })),
  medicines: async () => (await medicineMasterApi.list({ page_size: 500 })).items.map((m) => ({ value: m.id, label: m.name })),
  vaccines: async () => (await vaccineMasterApi.list({ page_size: 500 })).items.map((v) => ({ value: v.id, label: v.name })),
  expenseCategories: async () => (await expenseCategoriesApi.list({ page_size: 500 })).items.map((c) => ({ value: c.id, label: c.name })),
  suppliers: async () => (await suppliersApi.list({ page_size: 500 })).items.map((s) => ({ value: s.id, label: s.name })),
  // Labelled with party type (e.g. "Ramesh (Partner)") so the Milk Sales
  // customer picker reads clearly without a separate column.
  customers: async () => (await customersApi.list({ page_size: 500 })).items.map((c) => ({ value: c.id, label: `${c.name} (${c.party_type})`, meta: { partyType: c.party_type } })),
  users: async () => (await usersApi.list({ page_size: 500 })).items.map((u) => ({ value: u.id, label: u.username })),
  // For a field that stores a person's name as plain text (not a real FK to
  // Users) but should still be picked from the actual user list rather than
  // free-typed — the option's own value IS the username, so no id-to-name
  // resolution is needed anywhere the field is read back.
  userNames: async () => (await usersApi.list({ page_size: 500 })).items.map((u) => ({ value: u.username, label: u.username })),
  roles: async () => (await rolesApi.list({ page_size: 100 })).items.map((r) => ({ value: r.id, label: r.name })),
  farms: async () => (await farmMasterApi.list({ page_size: 50 })).items.map((f) => ({ value: f.id, label: f.farm_name })),
  sheds: async () => (await shedMasterApi.list({ page_size: 200 })).items.map((s) => ({ value: s.id, label: s.shed_name })),
  assetTypes: async () => (await assetTypesApi.list({ page_size: 200 })).items.map((t) => ({ value: t.id, label: t.name })),
  inventoryItems: async () => (await inventoryItemsApi.list({ page_size: 500 })).items.map((i) => ({ value: i.id, label: i.name })),
  inventoryLocations: async () => (await inventoryLocationsApi.list({ page_size: 200 })).items.map((i) => ({ value: i.id, label: i.name })),
  semenMaster: async () => (await semenMasterApi.list({ page_size: 500 })).items.map((s) => ({ value: s.id, label: s.bull_name || s.bull_code })),
  poultryBatches: async () => (await poultryBatchesApi.list({ page_size: 200 })).items.map((b) => ({ value: b.id, label: b.batch_no })),
  coconutTrees: async () => (await coconutTreesApi.list({ page_size: 500 })).items.map((t) => ({ value: t.id, label: t.tree_code })),
  coconutTreeGroups: async () => (await coconutTreesApi.groups()).map((g) => ({ value: g.tree_group, label: `${g.tree_group} (${g.tree_count} trees)` })),
  tasks: async () => (await tasksApi.list({ page_size: 500 })).items.map((t) => ({ value: t.id, label: t.title })),
  utilityMeters: async () => (await utilityMetersApi.list({ page_size: 50 })).items.map((m) => ({ value: m.id, label: `${m.meter_code} — ${m.meter_name}` })),
  // value is the hub's real id (a proper FK now - MilkSale.dairy_collection_
  // hub_id - unified on request; it used to be the hub's plain name, back
  // when MilkSession.collection_centre_name was a free string column).
  dairyCollectionHubs: async () => (await dairyCollectionHubsApi.list({ page_size: 200, active: true })).items.map((h) => ({ value: h.id, label: h.hub_code ? `${h.hub_code} — ${h.name}` : h.name })),
  subscriptionPlans: async () => (await subscriptionPlansApi.list({ page_size: 200, active: true })).items.map((p) => ({ value: p.id, label: `${p.name} (${p.litres_per_day}L @ ₹${p.rate_per_litre} — ${p.shift})` })),
  // Most recent 100 sessions, newest first - backs the optional "which
  // session is this Hub sale the distribution of" picker. meta.allocatedSaleId
  // lets that picker hide sessions another sale already holds.
  milkSessions: async () => (await milkSessionsApi.list({ page_size: 100 })).items.map((s) => ({
    value: s.id,
    label: `${s.session_code ?? s.id.slice(0, 8)} — ${s.production_date} · ${s.session_type} (${s.litres}L)`,
    meta: { allocatedSaleId: s.allocated_sale_id ?? null },
  })),
  // Every cow's milk per shift, last 14 days - backs the Hub sale's "Milk of
  // shift" picker. value is "YYYY-MM-DD|Morning".
  milkShifts: async () => (await milkSessionsApi.shifts()).map((s) => {
    const unsold = Number(s.unsold_litres);
    const sold = s.unsold_entries === 0 ? ` — sold (${s.sale_codes.join(', ')})` : s.sale_codes.length ? ` — ${formatNumber(unsold, 2)} L not sold yet` : '';
    return {
      value: `${s.production_date}|${s.session_type}`,
      label: `${formatDate(s.production_date)} · ${s.session_type} — ${s.animals} cow${s.animals === 1 ? '' : 's'}, ${formatNumber(s.litres, 2)} L${s.fat_percent ? ` @ ${formatNumber(s.fat_percent, 2)}% fat` : ''}${sold}`,
      meta: { unsold: s.unsold_entries, shiftSaleId: s.shift_sale_id ?? null },
    };
  }),
};

/** Loads a fixed set of reference/lookup lists (for form <select> options) in parallel via react-query. */
export function useReferenceData(keys: RefKey[]) {
  const results = useQueries({
    queries: keys.map((key) => ({
      queryKey: ['reference', key],
      queryFn: LOADERS[key],
      staleTime: 5 * 60_000,
    })),
  });

  const data = {} as Record<RefKey, RefOption[]>;
  keys.forEach((key, i) => {
    data[key] = (results[i].data as RefOption[] | undefined) ?? [];
  });
  const isLoading = results.some((r) => r.isLoading);

  return { data, isLoading };
}
